import React, { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { fmtDateIT } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, History, X } from "lucide-react";

const inp = "border border-slate-300 rounded-[3px] px-2 py-1.5 text-sm";

function RuleEditor({ rule, onChange }) {
  const set = (k, v) => onChange({ ...rule, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
      <label className="flex items-center gap-2"><input type="checkbox" checked={!!rule.set_recall} onChange={(e) => set("set_recall", e.target.checked)} /> Imposta recall</label>
      <div className="flex items-center gap-2">Mesi di calendario: <input type="number" min={0} max={60} value={rule.months} onChange={(e) => set("months", Number(e.target.value))} className={`${inp} w-20`} data-testid="rule-months" /></div>
      <div className="flex items-center gap-2">Base:
        <select value={rule.base} onChange={(e) => set("base", e.target.value)} className={inp} data-testid="rule-base">
          <option value="caricamento">Data caricamento pratica</option>
          <option value="rifiuto">Data effettiva del rifiuto</option>
        </select>
      </div>
      <div className="flex items-center gap-2">Portafoglio destinazione:
        <select value={rule.portale} onChange={(e) => set("portale", e.target.value)} className={inp} data-testid="rule-portale">
          <option value="">Invariato</option><option value="prestiti">Prestiti</option><option value="cessioni">Cessioni</option>
        </select>
      </div>
      <div className="flex items-center gap-2">Segnalato in CRIF:
        <select value={rule.crif} onChange={(e) => set("crif", e.target.value)} className={inp} data-testid="rule-crif">
          <option value="">Invariato</option><option value="SI">Sì</option><option value="NO">No</option><option value="ND">Non verificato</option>
        </select>
      </div>
      <label className="flex items-center gap-2"><input type="checkbox" checked={!!rule.crea_attivita} onChange={(e) => set("crea_attivita", e.target.checked)} /> Crea attività interna</label>
      {rule.crea_attivita && (
        <input value={rule.attivita_titolo || ""} onChange={(e) => set("attivita_titolo", e.target.value)} placeholder="Titolo attività" className={`${inp} sm:col-span-2`} />
      )}
      <div className="sm:col-span-2">
        <div className="text-[11px] text-slate-500 mb-1">Nota automatica (spiega l'azione effettuata)</div>
        <textarea value={rule.nota_auto || ""} onChange={(e) => set("nota_auto", e.target.value)} rows={2} className={`${inp} w-full`} data-testid="rule-nota" />
      </div>
    </div>
  );
}

function HistoryDialog({ reason, onClose }) {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.post("/reject-reasons/history/preview", { reason_id: reason.id }).then((r) => {
      setD(r.data);
      const s = {}; (r.data.items || []).forEach((it) => { if (!it.gia_aggiornata && it.collegata) s[it.practice_id] = true; });
      setSel(s);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Errore anteprima"));
  }, [reason.id]);

  const apply = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length) { toast.error("Seleziona almeno una pratica"); return; }
    setBusy(true);
    try {
      const r = await api.post("/reject-reasons/history/apply", { reason_id: reason.id, practice_ids: ids });
      setResults(r.data.results);
      toast.success("Applicazione allo storico completata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="history-dialog">
      <div className="bg-white rounded-[3px] w-full max-w-2xl shadow-xl flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Applica allo storico — {reason.label} (v{reason.version})</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-2 text-sm">
          {!d && <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />}
          {d && <div className="text-xs text-slate-500">{d.nota}</div>}
          {d && (d.items || []).map((it) => {
            const rec = it.anteprima?.azioni?.recall;
            return (
              <label key={it.practice_id} className="flex items-start gap-2 border border-slate-200 rounded-[3px] p-2" data-testid={`hist-row-${it.practice_id}`}>
                <input type="checkbox" checked={!!sel[it.practice_id]} disabled={it.gia_aggiornata}
                  onChange={(e) => setSel({ ...sel, [it.practice_id]: e.target.checked })} className="mt-1" />
                <div className="min-w-0 flex-1 text-xs">
                  <div className="font-medium text-slate-900">{it.cliente || it.elemento || "—"} · {it.monthKey}
                    {it.gia_aggiornata && <span className="text-emerald-700"> — già aggiornata a questa versione</span>}
                    {!it.collegata && <span className="text-amber-700"> — senza anagrafica: solo salvataggio motivo</span>}
                  </div>
                  <div className="text-slate-500">
                    {rec ? `Recall proposto ${fmtDateIT(rec.proposta)} (teorica ${fmtDateIT(rec.teorica)}${rec.dovuta ? ", DOVUTA" : ""})` : "Nessun recall previsto"}
                    {it.ultima_versione_eseguita ? ` · ultima esecuzione v${it.ultima_versione_eseguita} (${it.ultimo_stato_run})` : " · mai eseguita"}
                    {(it.anteprima?.warnings || []).some((w) => w.code === "recall_diverso") && " · recall esistente diverso: verrà MANTENUTO"}
                  </div>
                  {results && (() => { const r = results.find((x) => x.practice_id === it.practice_id); return r ? <div className={r.status === "done" ? "text-emerald-700" : "text-amber-700"}>Esito: {r.status}{r.gia_eseguita ? " (già eseguita)" : ""}{r.detail ? ` — ${r.detail}` : ""}</div> : null; })()}
                </div>
              </label>
            );
          })}
          {d && (d.items || []).length === 0 && <div className="text-slate-400 text-center py-4">Nessuna pratica con questo motivo</div>}
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex gap-2">
          <button onClick={onClose} className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px]">Chiudi</button>
          <button onClick={apply} disabled={busy || !d} data-testid="hist-apply"
            className="flex-1 px-3 py-2 text-sm bg-[#0A192F] text-white rounded-[3px] disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Applica alle selezionate
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RejectReasonsPage() {
  const [items, setItems] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [edit, setEdit] = useState(null); // {id, label, rule}
  const [hist, setHist] = useState(null);

  const load = useCallback(() => {
    api.get("/reject-reasons").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (newLabel.trim().length < 2) return;
    try { await api.post("/reject-reasons", { label: newLabel.trim(), rule: { set_recall: true, months: 3, base: "rifiuto", portale: "", crif: "", crea_attivita: false, attivita_titolo: "", nota_auto: "" } }); setNewLabel(""); load(); toast.success("Motivo creato"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const patch = async (id, body, msg) => {
    try { await api.patch(`/reject-reasons/${id}`, body); load(); if (msg) toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Eliminare definitivamente "${r.label}"? Possibile solo se mai utilizzato.`)) return;
    try { await api.delete(`/reject-reasons/${r.id}`); load(); toast.success("Motivo eliminato"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <AppLayout title="Motivi di rifiuto e automazioni" subtitle="Impostazioni · solo amministratori">
      <div className="max-w-3xl space-y-3" data-testid="reject-reasons-page">
        <div className="text-xs bg-slate-50 border border-slate-200 rounded-[3px] p-2.5 text-slate-600">
          Id stabili: rinominare non rompe regole o collegamenti. Un motivo usato resta nello storico anche se disattivato.
          Le modifiche alle regole valgono per i NUOVI rifiuti: per lo storico usa "Applica allo storico" (con anteprima).
          Le automazioni non inviano messaggi e non presentano richieste di finanziamento. Zero AI.
        </div>
        <div className="flex gap-2">
          <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Nuovo motivo…" data-testid="reason-new-label"
            className={`${inp} flex-1`} onKeyDown={(e) => e.key === "Enter" && add()} />
          <button onClick={add} data-testid="reason-add" className="px-3 py-2 text-sm bg-[#0A192F] text-white rounded-[3px] inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Aggiungi</button>
        </div>
        {!items && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
        {(items || []).map((r) => (
          <div key={r.id} className={`bg-white border rounded-[3px] p-3 ${r.active ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`reason-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              {edit?.id === r.id ? (
                <input value={edit.label} onChange={(e) => setEdit({ ...edit, label: e.target.value })} className={`${inp} flex-1 min-w-[200px]`} data-testid="reason-edit-label" />
              ) : (
                <span className="font-semibold text-sm text-slate-900">{r.label}</span>
              )}
              <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-slate-100 text-slate-600">v{r.version} · usato {r.usati} volte</span>
              {!r.active && <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-amber-100 text-amber-800">disattivato</span>}
              <div className="ml-auto flex items-center gap-1.5">
                {edit?.id === r.id ? (
                  <>
                    <button onClick={() => { patch(r.id, { label: edit.label, rule: edit.rule }, "Motivo aggiornato"); setEdit(null); }} data-testid="reason-save"
                      className="px-2.5 py-1.5 text-xs bg-[#0A192F] text-white rounded-[3px]">Salva</button>
                    <button onClick={() => setEdit(null)} className="px-2.5 py-1.5 text-xs border border-slate-300 rounded-[3px]">Annulla</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setEdit({ id: r.id, label: r.label, rule: { ...r.rule } })} data-testid={`reason-edit-${r.id}`}
                      className="px-2.5 py-1.5 text-xs border border-slate-300 rounded-[3px] hover:bg-slate-50">Modifica</button>
                    <button onClick={() => patch(r.id, { active: !r.active }, r.active ? "Motivo disattivato (storico conservato)" : "Motivo riattivato")}
                      className="px-2.5 py-1.5 text-xs border border-slate-300 rounded-[3px] hover:bg-slate-50" data-testid={`reason-toggle-${r.id}`}>{r.active ? "Disattiva" : "Riattiva"}</button>
                    <button onClick={() => setHist(r)} className="px-2.5 py-1.5 text-xs border border-slate-300 rounded-[3px] hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`reason-history-${r.id}`}>
                      <History className="w-3 h-3" /> Applica allo storico
                    </button>
                    {r.usati === 0 && <button onClick={() => del(r)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-[3px]" data-testid={`reason-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                  </>
                )}
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-slate-100">
              {edit?.id === r.id
                ? <RuleEditor rule={edit.rule} onChange={(rule) => setEdit({ ...edit, rule })} />
                : (
                  <div className="text-xs text-slate-600">
                    {r.rule?.set_recall ? `Recall: ${r.rule.base === "caricamento" ? "data caricamento" : "data rifiuto"} +${r.rule.months} mesi di calendario` : "Nessun recall"}
                    {r.rule?.portale && ` · Portafoglio → ${r.rule.portale}`}
                    {r.rule?.crif && ` · Segnalato in CRIF → ${r.rule.crif}`}
                    {r.rule?.crea_attivita && ` · Attività interna`}
                    {r.rule?.nota_auto && <div className="text-slate-500 mt-0.5">Nota: "{r.rule.nota_auto}"</div>}
                  </div>
                )}
            </div>
          </div>
        ))}
      </div>
      {hist && <HistoryDialog reason={hist} onClose={() => { setHist(null); load(); }} />}
    </AppLayout>
  );
}
